import * as THREE from 'three';
import { BeatSequencer } from '@/story/sequencer';
import { OTTO_BEAT, OTTO_CUES, OTTO_FLAGS, OTTO_IDS } from '@/story/act1/otto';
import {
  FAR_LAMP_NAME,
  FATTENING_SPAWN,
  HIGH_BAY_INTENSITY,
  INSPECTION_LAMP_NAME,
  OTTO_AT,
  OTTO_GAP_AT,
  OTTO_PEN,
  STRAW_PEN,
  VENT_AT,
  VENT_REACH,
  penBounds,
} from '@/world/fattening';
import { OttoDirector } from '@/world/ottoDirector';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

/**
 * How much rolling in the straw ends beat 3's last stage.
 *
 * Twelve metres is three or four good runs across a 4.8 m pen. It lives here
 * rather than in `story/act1/otto.ts` because the beat is not allowed to know
 * how big a pen is — it waits on a flag, and this is the room's answer to it.
 */
export const OTTO_ROLL_TARGET = 12;

type Rect = ReturnType<typeof penBounds>;
const inside = (rect: Rect, at: { readonly x: number; readonly z: number }): boolean =>
  at.x >= rect.minX && at.x <= rect.maxX && at.z >= rect.minZ && at.z <= rect.maxZ;

/**
 * Act 1 beat 3, *Otto*.
 *
 * Until session 84 this was a branch in `main.ts` plus lines in five other
 * places there (D-230). Where each piece now runs, against where it ran: the
 * five place flags and the roll counter ran in the fixed step just after the
 * controller, and the flank nudge in the interaction-event loop; both are now
 * in `fixedStep`, after interaction events. All of it is still before
 * `story.update` in the same tick, so the story sees the same flags on the same
 * tick. The roll counter reads `tick.motion`, which is the same
 * `current - previous` the branch read.
 *
 * **Look at how short this is, and then look at beat 2.** That is not an
 * unfinished job. Beat 2 needs a worker on a route, a stealth field, a light
 * grid, a cover set, a ray callback and a collider that moves, because being
 * seen is its subject. Beat 3 deliberately registers no `Watcher` at all —
 * spec §5.4 wants one consequence-free rolling space in Act 1, *before*
 * pressure teaches the player never to touch the game's best-feeling verb. If a
 * later session finds itself adding a watcher here "for atmosphere", it has to
 * argue with §5.4 first.
 */
function createOttoPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 3 / part 1: the host offered no world capability');
  const ottoGroup = world.room.otto;
  if (ottoGroup === undefined) throw new Error('beat 3 / part 1: the room offers no Otto — booted outside Act 1\'s fattening hall');
  // From `penBounds`, never re-derived: two lists built from the same constants
  // a few hundred lines apart is precisely the trap D-056 and D-059 recorded,
  // one room earlier. His pen has two customers wanting it for opposite
  // reasons: `fixedStep` raises `inHisPen` from it, and the director uses it to
  // decide whether he is still looking at the way in or has started looking at her.
  const strawPen = penBounds(STRAW_PEN);
  const hisPen = penBounds(OTTO_PEN);
  // Found by name in the scene: the lighting rig builds them, not the room
  // module. Nothing found is a renamed fixture, and beat 3's two pointers both
  // hang off these lights — so it throws rather than quietly playing the
  // version of the beat that Alex reported had no goal in it. See D-087.
  const namedLight = (name: string): THREE.Light => {
    const found = world.scene.getObjectByName(name);
    if (!(found instanceof THREE.Light)) {
      throw new Error(`beat 3 needs the "${name}" fixture and the hall did not build one`);
    }
    return found;
  };
  const director = new OttoDirector(
    {
      otto: ottoGroup,
      strawPen: { x: (strawPen.minX + strawPen.maxX) / 2, z: STRAW_PEN.z },
      // The broken corner, in world XZ, from the room rather than from
      // arithmetic written here. It is the only thing he points at that she
      // can act on, and if this and the hole ever disagreed he would be
      // inviting her through a slab of concrete.
      gap: OTTO_GAP_AT,
      hisPen,
      vent: { x: VENT_AT.x, z: VENT_AT.z },
      penLamp: namedLight(INSPECTION_LAMP_NAME),
      farLamp: namedLight(FAR_LAMP_NAME),
      farLampLit: HIGH_BAY_INTENSITY,
    },
    {
      onThump: (at) => {
        // The loudest thing in the beat, and the only sound in it that is a
        // *cost*. Detuned well down from the boot: three hundred kilograms of
        // old plush hitting bedding is not a footstep, it is a drum.
        world.playOneShot('footfall-concrete', { gain: 0.85, position: at, detune: -1100 });
      },
      onGreet: (at) => {
        world.playOneShot('footfall-concrete', { gain: 0.28, position: at, detune: -820 });
      },
    },
  );
  // The generic fade (D-230): the director's own overlay was `BeatFadeDirector`
  // with Otto's two cue ids baked in. Its `open` snapped to black first, which
  // the generic one does not; the overlay is built black and `open` is the
  // beat's first cue, so nothing is on screen for the difference.
  const fade = capabilities.createFade({ open: OTTO_CUES.open, fadeOut: OTTO_CUES.fadeOut });
  const story = new BeatSequencer(OTTO_BEAT);
  const interactables: readonly Interactable[] = [
    {
      id: OTTO_IDS.otto,
      // Head height on a 1.06 m animal, so `focus` composes on his face rather
      // than on his shoulder. He does not move, so unlike beat 2's worker this
      // one is authored once and never re-pointed.
      at: { x: OTTO_AT.x, y: 0.85, z: OTTO_AT.z },
      verbs: ['observe'],
      mapGlyph: 'pig',
      // Long, and deliberately. `focus` is the reward for holding it (D-066)
      // and the demonstration is what she is holding it *for* — a two-second
      // completion would take the camera off him a beat before he moves.
      duration: 3.6,
      // 6 m. He is across a 2.2 m alley behind a 1.05 m wall she cannot see
      // over (D-071), so "within reach" here means "anywhere in the passage
      // beside his pen" rather than "nose to nose" — which she cannot get.
      reach: 6,
      enabled: false,
    },
    {
      id: OTTO_IDS.flank,
      // His shoulder, which is the middle of him. On his centre-line rather
      // than his alley-side flank, and that is the whole safety of this entry.
      at: { x: OTTO_AT.x, y: 0.55, z: OTTO_AT.z - 0.45 },
      verbs: ['nudge'],
      // **0.9 m, and it is chosen against two distances rather than picked.**
      //
      // From the alley the closest she can get to his centre-line is about
      // 0.98 m in X — the wall face at 1.10 less her 0.125 m capsule radius —
      // which puts her 1.22 m away and OUTSIDE this reach. From inside the pen
      // his measured body is 0.82 m across, so its alley-side face sits at
      // about 1.79 m and her centre stops near 1.665 m: **0.535 m away**, well
      // inside it. (Those inside numbers were 1.77 and 0.44 when this was
      // written from an estimate rather than from his mesh; a review measured
      // them. The conclusion did not move, the arithmetic did.)
      //
      // That gap is the point: without it she could pat him from across the
      // passage through a slab of concrete, and nothing in the interaction
      // system would object, because a reach is all it knows. `nudge` takes
      // `reachSlack` 1.0, so this number is literal — `observe`'s 3.0 would
      // have made it 2.7 m and worthless. **`otto.spec.ts` asserts both
      // halves**: reachable from inside, unreachable from the alley.
      reach: 0.9,
      enabled: false,
    },
  ];
  /**
   * Metres she has rolled *inside* the hospital pen, cumulative. Simulation
   * state, and the only number beat 3 keeps; see `fixedStep` for why it is a
   * distance rather than a timer or a count of rolls.
   */
  let rolledInStraw = 0;
  let disposed = false;

  return {
    story,
    interactables,
    get fade(): number { return fade.fade; },
    // The legacy prompt list had no Otto term, so the verb prompt stayed up
    // over the closing black. Every other part hides it past half-black; so
    // does this one now (D-230).
    get promptSuppressed(): boolean { return !disposed && fade.fade > 0.5; },
    fixedStep({ player, raiseFlag, nudged, motion }): void {
      // Every flag here is a fact about where she is, handed to the beat the
      // way beat 2 hands over its three. None is a `Watcher` and none is a
      // consequence: "is she far enough in", "close enough", "in HIS pen", "in
      // the straw", "rolled enough", "at the vent". Spec §5.4 wants a
      // consequence-free space, and this is what that looks like written down.
      if (player.z >= FATTENING_SPAWN.z + 4) raiseFlag(OTTO_FLAGS.inHall);
      if (Math.hypot(player.x - OTTO_AT.x, player.z - OTTO_AT.z) <= 5) raiseFlag(OTTO_FLAGS.atHisPen);
      // She is in **his** pen, through the broken corner of its mouth. The
      // same rectangle test as the straw pen, because the two questions are
      // the same question about two pens.
      if (inside(hisPen, player)) raiseFlag(OTTO_FLAGS.inHisPen);
      const inStraw = inside(strawPen, player);
      if (inStraw) raiseFlag(OTTO_FLAGS.inStrawPen);
      // An ending that is a place she walked to rather than a timer that
      // expired, as beat 2's `atDoor`.
      if (Math.hypot(player.x - VENT_AT.x, player.z - VENT_AT.z) <= VENT_REACH) raiseFlag(OTTO_FLAGS.atVent);

      // ── The roll counter ────────────────────────────────────────────────
      //
      // **Distance, not time and not a count of rolls**, measured on the fixed
      // step rather than from the animator, which runs at display rate
      // (D-045). It only accrues *inside the pen*, so a player who rolls the
      // length of the alley to get here is not credited for the trip.
      //
      // The actual step (`motion`) rather than `speed * dt`, because the
      // controller clamps and slides her against the pen wall and a roll that
      // spends two seconds grinding along it has covered almost no ground. The
      // gate is meant to measure rolling *about*, not rolling *at*.
      if (inStraw && motion?.gait === 'roll') {
        rolledInStraw += Math.hypot(motion.dx, motion.dz);
        if (rolledInStraw >= OTTO_ROLL_TARGET) raiseFlag(OTTO_FLAGS.rolledEnough);
      }

      if (nudged?.(OTTO_IDS.flank) === true) {
        // **She has touched him, and that is what starts the demonstration.**
        // The `in-with-him` stage waits on this flag, so the nine seconds that
        // follow happen because she asked rather than because a clock ran out.
        // Alex, on the version without it: *"beat 3 is a bit too distant when
        // it comes to recognize otto."* One nudge — several would turn a
        // greeting into a task.
        raiseFlag(OTTO_FLAGS.nudged);
        // The same short, low, pleased one-shot the director plays when he
        // first notices her, at his head. **He has no voice of his own yet**
        // and that is deliberate: a vocal set for Otto is a `check:audio` job
        // with its own gate. Filed.
        world.playOneShot('footfall-concrete', { gain: 0.28, position: { x: OTTO_AT.x, y: 0.7, z: OTTO_AT.z }, detune: -820 });
      }
    },
    handleCue(cue): void {
      fade.handle(cue);
      director.handle(cue as Parameters<OttoDirector['handle']>[0]);
      // `offerFlank` is the one that matters: until it, the nudge that starts
      // the demonstration is not a way to reach it. See `OTTO_CUES.offerFlank`.
      if (cue.id === OTTO_CUES.offerOtto) world.interaction.setEnabled(OTTO_IDS.otto, true);
      if (cue.id === OTTO_CUES.offerFlank) world.interaction.setEnabled(OTTO_IDS.flank, true);
    },
    renderStep(frameDt, her): void {
      if (disposed) return;
      // Clamped as the director clamps its own delta, which is what the
      // overlay was fed while it lived there.
      fade.update(Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0);
      // Her position and nothing else; see `ottoDirector.ts` on why it is
      // handed in rather than reached for.
      director.update(frameDt, her);
    },
    diagnostics() {
      return {
        kind: 'otto',
        otto: {
          demoProgress: director.demoProgress,
          attention: director.attention,
          penLamp: director.penLampIntensity,
          farLamp: director.farLampIntensity,
          rolledInStraw,
          rollTarget: OTTO_ROLL_TARGET,
          fade: fade.fade,
          ended: director.ended,
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      director.dispose();
      fade.dispose();
    },
  };
}

export const OTTO_PART_MODULE: BeatPartModuleDefinition = {
  beat: 3,
  part: 1,
  room: 'fattening',
  create: createOttoPartModule,
};
